import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, ClipboardList, FileText, PackageSearch, XCircle, SlidersHorizontal, Pencil, Banknote, Smartphone, CreditCard, Ban, Plus } from "lucide-react";
import { paymentButtonClass, PaymentModeButtons } from "../lib/paymentMethods";
import { ProductPicker } from "../components/ProductPicker";
import { CustomerPicker } from "../components/CustomerPicker";
import { ArticlesWindow } from "../components/ArticlesWindow";
import { InvoiceUiCustomizer } from "../components/InvoiceUiCustomizer";
import { useDocLock } from "../lib/locks";
import { LockBanner } from "../components/LockBanner";
import { useInvoiceUi } from "../hooks/useInvoiceUi";
import { makeMoney, FONT_CLASSES, DENSITY } from "../lib/invoiceUi";

const toInputDate = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const parseNum = (v) => parseFloat(String(v).replace(",", "."));

const PAYMENT_METHODS = [
  { value: "non_paye", label: "NON PAYÉ", icon: Ban },
  { value: "especes", label: "ESPECE", icon: Banknote, primary: true },
  { value: "wave", label: "WAVE", icon: Smartphone, primary: true },
  { value: "orange_money", label: "ORANGE MONEY", icon: Smartphone, primary: true },
  { value: "cheque", label: "Chèque", icon: CreditCard },
  { value: "banque", label: "Banque", icon: CreditCard },
];

const PurchaseEntryPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const { user, isAdmin } = useAuth();
  const { ui, update: updateUi, save: saveUi, reset: resetUi, revert: revertUi } = useInvoiceUi();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const pickerRef = useRef(null);
  const winRef = useRef(null);
  const linesRef = useRef(null);
  const qtyRefs = useRef({});
  const priceRefs = useRef({});
  const [focusTarget, setFocusTarget] = useState(null);
  const [winOpen, setWinOpen] = useState(false);
  const [orderDate, setOrderDate] = useState(() => toInputDate(new Date()));
  const [supplierRef, setSupplierRef] = useState("");
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [depotId, setDepotId] = useState("");
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("non_paye");
  const [amountPaid, setAmountPaid] = useState(0);
  const [editing, setEditing] = useState(null);
  const { lockedBy } = useDocLock("order", editId);
  const [lastPurchase, setLastPurchase] = useState({});
  const [nextNumber, setNextNumber] = useState("");
  const [partial, setPartial] = useState(false);
  const [pending, setPending] = useState({ product: null, qty_raw: "", price_raw: "" });
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({ name: "", phone: "" });
  const pendingQtyRef = useRef(null);
  const pendingPriceRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s, d, lp, nn] = await Promise.all([api.get("/products"), api.get("/suppliers"), api.get("/depots"), api.get("/products/last-purchase").catch(() => ({ data: {} })), api.get("/orders/next-number").catch(() => ({ data: { number: "" } }))]);
        setProducts(p.data); setSuppliers(s.data); setDepots(d.data); setLastPurchase(lp.data || {}); setNextNumber(nn.data.number);
        if (editId) {
          const { order } = (await api.get(`/orders/${editId}`)).data;
          if (!["pending", "ordered"].includes(order.status) || order.is_invoiced) { toast.error("Cet achat ne peut plus être modifié (réceptionné, facturé ou annulé)"); navigate(`/purchase/${editId}`); return; }
          const byId = Object.fromEntries(p.data.map((x) => [x.id, x]));
          setEditing(order);
          setSupplierId(order.supplier_id); setDepotId(order.depot_id || ""); setSupplierRef(order.supplier_ref || "");
          setOrderDate(order.order_date || toInputDate(order.created_at));
          setCart(order.items.map((i) => ({ product_id: i.product_id, product_name: i.product_name, sku: byId[i.product_id]?.sku || "", unit: byId[i.product_id]?.unit || i.unit || "", quantity: i.quantity, qty_raw: String(i.quantity), unit_cost: i.unit_cost, price_raw: String(i.unit_cost), total: i.total, stock: byId[i.product_id]?.stock ?? 0 })));
        } else setDepotId(d.data[0]?.id || "");
      } catch { toast.error("Erreur lors du chargement des données"); }
      finally { setLoading(false); setTimeout(() => pickerRef.current?.focus(), 50); }
    };
    load();
    // eslint-disable-next-line
  }, [editId]);

  useEffect(() => {
    if (!focusTarget) return;
    const el = focusTarget.field === "qty" ? qtyRefs.current[focusTarget.id] : focusTarget.field === "price" ? priceRefs.current[focusTarget.id] : focusTarget.field === "pqty" ? pendingQtyRef.current : focusTarget.field === "pprice" ? pendingPriceRef.current : null;
    if (el) { el.focus(); el.select(); }
    else if (focusTarget.field === "picker") pickerRef.current?.focus();
    else if (focusTarget.field === "list") winRef.current?.focus();
    setFocusTarget(null);
  }, [focusTarget, cart]);

  useEffect(() => { if (linesRef.current) linesRef.current.scrollTop = linesRef.current.scrollHeight; }, [cart.length]);

  const focusList = useCallback(() => { setWinOpen(true); setFocusTarget({ field: "list" }); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "F4") { e.preventDefault(); focusList(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusList]);

  const updateItem = (productId, changes) => {
    setCart(cart.map((item) => {
      if (item.product_id !== productId) return item;
      const next = { ...item, ...changes };
      next.quantity = Math.max(0.01, next.quantity);
      next.unit_cost = Math.max(0, next.unit_cost);
      next.total = Math.round(next.quantity * next.unit_cost * 100) / 100;
      if (changes.quantity !== undefined && changes.qty_raw === undefined) next.qty_raw = String(next.quantity);
      if (changes.unit_cost !== undefined && changes.price_raw === undefined) next.price_raw = String(next.unit_cost);
      return next;
    }));
  };

  const addToCart = (product, focusQty = false, qty = 1, cost = null) => {
    const existing = cart.find((i) => i.product_id === product.id);
    const unitCost = cost !== null && !isNaN(cost) ? cost : (lastPurchase[product.id]?.unit_cost ?? product.cost_price ?? 0);
    if (existing) updateItem(product.id, { quantity: existing.quantity + qty, unit_cost: unitCost });
    else setCart([...cart, { product_id: product.id, product_name: product.name, sku: product.sku, unit: product.unit, quantity: qty, qty_raw: String(qty), unit_cost: unitCost, price_raw: String(unitCost), total: Math.round(qty * unitCost * 100) / 100, stock: product.stock }]);
    if (focusQty) { setSearchTerm(""); setFocusTarget({ id: product.id, field: "qty" }); }
  };

  // Ligne de saisie : Référence → Quantité → Prix d'achat → Ajouter
  const pickPending = (product) => {
    const cost = lastPurchase[product.id]?.unit_cost ?? product.cost_price ?? 0;
    setPending({ product, qty_raw: "1", price_raw: String(cost) });
    setSearchTerm(product.name);
    setFocusTarget({ field: "pqty" });
  };
  const commitPending = () => {
    if (!pending.product) { toast.error("Choisissez d'abord un article"); return; }
    const q = parseNum(pending.qty_raw);
    if (isNaN(q) || q <= 0) { toast.error("Quantité invalide"); setFocusTarget({ field: "pqty" }); return; }
    const c = parseNum(pending.price_raw);
    addToCart(pending.product, false, q, isNaN(c) ? null : c);
    setPending({ product: null, qty_raw: "", price_raw: "" });
    setSearchTerm("");
    setFocusTarget({ field: "picker" });
  };
  const createQuickSupplier = async () => {
    if (!quick.name.trim()) return;
    try {
      const r = await api.post("/suppliers", { name: quick.name.trim(), phone: quick.phone.trim() });
      setSuppliers([...suppliers, r.data]); setSupplierId(r.data.id); setQuickOpen(false); setQuick({ name: "", phone: "" });
      toast.success(`Fournisseur ${r.data.name} créé`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création du fournisseur"); }
  };

  const handleBarcode = async (code) => {
    try { addToCart((await api.get(`/products/barcode/${code}`)).data); }
    catch { const p = products.find((x) => x.sku.toLowerCase() === code.toLowerCase() || x.barcode === code); if (p) addToCart(p); else toast.error("Produit non trouvé"); }
  };

  const onQtyChange = (item, raw) => { const n = parseNum(raw); if (!isNaN(n) && n > 0) updateItem(item.product_id, { quantity: n, qty_raw: raw }); else setCart(cart.map((c) => (c.product_id === item.product_id ? { ...c, qty_raw: raw } : c))); };
  const onPriceChange = (item, raw) => { const n = parseNum(raw); if (!isNaN(n) && n >= 0) updateItem(item.product_id, { unit_cost: n, price_raw: raw }); else setCart(cart.map((c) => (c.product_id === item.product_id ? { ...c, price_raw: raw } : c))); };
  const normalize = (item) => updateItem(item.product_id, { quantity: item.quantity, unit_cost: item.unit_cost });
  const removeFromCart = (id) => setCart(cart.filter((i) => i.product_id !== id));
  const resetAll = () => {
    if (editing) { navigate(`/purchase/${editing.id}`); return; }
    setCart([]); setSupplierId(""); setSupplierRef(""); setPaymentMethod("non_paye"); setAmountPaid(0); setPartial(false); setPending({ product: null, qty_raw: "", price_raw: "" }); setSearchTerm(""); setFocusTarget({ field: "picker" });
  };

  const total = cart.reduce((s, i) => s + i.total, 0);
  const isUnpaid = paymentMethod === "non_paye";
  const effectivePaid = editing ? (editing.amount_paid || 0) : isUnpaid ? 0 : partial ? Math.min(amountPaid, total) : total;
  const balanceDue = total - effectivePaid;
  const totalQty = cart.reduce((s, i) => s + i.quantity, 0);
  const hasSupplier = supplierId && supplierId !== "comptoir";
  const supplier = suppliers.find((s) => s.id === supplierId);

  const handleSave = async () => {
    if (lockedBy) { toast.error(lockedBy); return; }
    if (!hasSupplier) { toast.error("Sélectionnez un fournisseur"); return; }
    if (cart.length === 0) { toast.error("La facture d'achat est vide"); return; }
    if (editing && total < (editing.amount_paid || 0) - 0.01) { toast.error(`Le total ne peut pas être inférieur au montant déjà payé (${money(editing.amount_paid)})`); return; }
    setProcessing(true);
    try {
      const items = cart.map(({ product_id, product_name, quantity, unit_cost, total }) => ({ product_id, product_name, quantity, unit_cost, total }));
      if (editing) {
        await api.put(`/orders/${editing.id}`, { supplier_id: supplierId, depot_id: depotId || "", items, notes: editing.notes || "", order_date: orderDate, supplier_ref: supplierRef });
        toast.success(`Achat ${editing.order_number} modifié`);
        navigate(`/purchase/${editing.id}`);
        return;
      }
      const r = await api.post("/orders", {
        supplier_id: supplierId, depot_id: depotId || "", items, notes: "", order_date: orderDate, supplier_ref: supplierRef,
        payment_method: isUnpaid ? "especes" : paymentMethod, amount_paid: effectivePaid,
      });
      toast.success(`Achat ${r.data.order_number} enregistré${effectivePaid > 0 ? ` — ${money(effectivePaid)} réglés` : ""}`);
      navigate(`/purchase/${r.data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur lors de l'enregistrement de l'achat");
    } finally { setProcessing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  const L = ui.labels;
  const money = makeMoney(ui.format);
  const fontClass = FONT_CLASSES[ui.format.fontSize] || "text-sm";
  const dz = DENSITY[ui.format.density] || DENSITY.compact;
  const inp = "h-10 rounded-md bg-white border-[#a9d9c6] focus-visible:ring-[#7fcbb7] text-sm";
  const saveDisabled = cart.length === 0 || processing || !hasSupplier;
  const depotName = depots.find((d) => d.id === depotId)?.name || "";
  const onCardKeyDown = (e) => { if (e.key === "Enter" && e.ctrlKey && !saveDisabled) { e.preventDefault(); handleSave(); } };

  const lineRow = (item, idx) => (
    <tr key={item.product_id} className="border-b border-[#dcefe6] bg-white hover:bg-[#f3faf7]" data-testid={`purchase-line-${item.product_id}`}>
      <td className={`px-3 ${dz.cell} font-mono text-xs text-slate-500`}>{item.sku || idx + 1}</td>
      <td className={`px-3 ${dz.cell}`}>
        <input type="text" value={item.product_name} onChange={(e) => setCart(cart.map((c) => (c.product_id === item.product_id ? { ...c, product_name: e.target.value } : c)))} onFocus={(e) => e.target.select()} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFocusTarget({ id: item.product_id, field: "qty" }); } }} title="Désignation modifiable sur ce document (la fiche article ne change pas)" className="w-full bg-transparent font-medium text-slate-900 leading-tight border-0 border-b border-dashed border-transparent hover:border-teal-400 focus:border-teal-500 focus:outline-none px-0 py-0.5" data-testid={`name-input-${item.product_id}`} />
        {ui.format.density !== "compact" && <p className="text-[11px] text-slate-400 font-mono">stock {item.stock}</p>}
      </td>
      <td className={`px-3 ${dz.cell} text-slate-600 text-sm`}>{item.unit || "—"}</td>
      <td className={`px-2 ${dz.cell}`}>
        <Input ref={(el) => { qtyRefs.current[item.product_id] = el; }} type="text" inputMode="decimal" value={item.qty_raw} onChange={(e) => onQtyChange(item, e.target.value)} onBlur={() => normalize(item)} onFocus={(e) => e.target.select()}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFocusTarget({ id: item.product_id, field: "price" }); } }}
          className={`w-full ${dz.input} text-right font-mono rounded-md border-[#a9d9c6] px-2`} data-testid={`purchase-qty-${item.product_id}`} />
      </td>
      <td className={`px-2 ${dz.cell} text-right`}>
        <Input ref={(el) => { priceRefs.current[item.product_id] = el; }} type="text" inputMode="decimal" value={item.price_raw} onChange={(e) => onPriceChange(item, e.target.value)} onBlur={() => normalize(item)} onFocus={(e) => e.target.select()}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFocusTarget({ field: "picker" }); } }}
          className={`w-full ${dz.input} text-right font-mono rounded-md border-[#a9d9c6] px-2`} data-testid={`purchase-cost-${item.product_id}`} />
      </td>
      <td className={`px-3 ${dz.cell} text-right font-mono font-semibold text-slate-900`}>{money(item.total)}</td>
      <td className={`px-2 ${dz.cell} text-right`}>
        <Button variant="ghost" size="sm" tabIndex={-1} onClick={() => removeFromCart(item.product_id)} className="w-7 h-7 p-0 text-red-600" data-testid={`purchase-remove-${item.product_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </td>
    </tr>
  );

  return (
    <div className="-m-6 lg:-m-8 p-6 lg:p-8 min-h-screen bg-[#f6f4ee] space-y-4" data-testid="purchase-entry-page">
      <LockBanner message={lockedBy} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight text-slate-900 flex items-center gap-2">
            {editing ? <><Pencil className="w-5 h-5 text-teal-700" /> MODIFICATION DE L'ACHAT <span className="font-mono text-teal-700">{editing.order_number}</span></> : "FACTURE D'ACHAT"}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{depotName ? `Dépôt : ${depotName} · ` : ""}Établie par {editing?.created_by || user?.name}{cart.length ? ` · ${cart.length} ligne${cart.length > 1 ? "s" : ""}` : ""} · Le stock sera mis à jour à la réception</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/orders"><Button variant="outline" className="rounded-md bg-white" data-testid="purchase-docs-link"><FileText className="w-4 h-4 mr-2" /> Documents des achats</Button></Link>
          {isAdmin && <Button variant="outline" className="rounded-md bg-white" onClick={() => setCustomizeOpen(true)} data-testid="customize-invoice-ui-btn"><SlidersHorizontal className="w-4 h-4 mr-2" /> Personnaliser l'écran</Button>}
        </div>
      </div>

      <Card className="bg-white border border-[#e4e0d4] rounded-xl shadow-sm" onKeyDown={onCardKeyDown} data-testid="purchase-card">
        <CardContent className="p-5 lg:p-7 space-y-5">
          <div className="flex items-start justify-between gap-4 bg-[#f6f4ee] rounded-lg px-5 py-4" data-testid="purchase-title-band">
            <div>
              <h2 className="font-serif text-2xl text-slate-900 leading-tight">{editing ? "Modification de l'achat" : "Facture d'achat"}</h2>
              <p className="text-sm text-slate-500 mt-1">{editing ? "Modifiez le fournisseur, les lignes, les quantités ou les prix — le règlement déjà effectué est conservé." : "Plusieurs lignes possibles · numéro attribué à la validation"}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">N° document</p>
              <p className="font-mono text-xl font-bold text-slate-900" data-testid="purchase-next-number">{editing ? editing.order_number : nextNumber || "—"}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Dépôt de destination</Label>
              <Select value={depotId || "none"} onValueChange={(v) => setDepotId(v === "none" ? "" : v)}>
                <SelectTrigger className={inp} data-testid="purchase-depot"><SelectValue placeholder="Dépôt" /></SelectTrigger>
                <SelectContent>{depots.length === 0 && <SelectItem value="none">— Aucun dépôt —</SelectItem>}{depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Fournisseur</Label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0"><CustomerPicker customers={suppliers} value={supplierId} onChange={setSupplierId} placeholder="Taper le nom ou le téléphone du fournisseur…" testId="select-purchase-supplier" noneLabel={null} clearTitle="Changer de fournisseur" balanceLabels={{ positive: "À payer", negative: "Avoir" }} /></div>
                <Button type="button" variant="outline" onClick={() => setQuickOpen(true)} className={`${inp} w-10 px-0 shrink-0`} title="Nouveau fournisseur" data-testid="quick-supplier-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Téléphone</Label>
              <Input value={supplier?.phone || ""} readOnly placeholder="—" className={`${inp} bg-[#f3f7f4] text-slate-700`} data-testid="purchase-supplier-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Date d'achat (jour)</Label>
                <Input type="date" value={orderDate} max={toInputDate(new Date())} onChange={(e) => setOrderDate(e.target.value)} className={`${inp} font-mono`} data-testid="purchase-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">N° facture / BL fournisseur</Label>
                <Input value={supplierRef} onChange={(e) => setSupplierRef(e.target.value)} placeholder="Facultatif" className={`${inp} font-mono`} data-testid="purchase-supplier-ref" />
              </div>
            </div>
          </div>

          <div className="bg-[#f3f1ea] rounded-lg p-2" data-testid="purchase-lines-wrap">
            <div ref={linesRef} className="max-h-[calc(100vh-560px)] min-h-[120px] overflow-y-auto border border-[#8fcdb6] rounded-md bg-white">
              <table className={`w-full ${fontClass}`} data-testid="purchase-lines-table">
                <thead className="sticky top-0 z-10">
                  <tr className="[&>th]:border-r [&>th]:border-[#8fcdb6] [&>th:last-child]:border-r-0 border-b border-[#8fcdb6]">
                    <th className={`px-3 ${dz.head} text-left font-bold w-[16%]`}>Référence</th>
                    <th className={`px-3 ${dz.head} text-left font-bold`}>Désignation</th>
                    <th className={`px-3 ${dz.head} text-left font-bold w-[12%]`}>Unité</th>
                    <th className={`px-3 ${dz.head} text-right font-bold w-[13%]`}>Quantité</th>
                    <th className={`px-3 ${dz.head} text-right font-bold w-[13%]`}>Prix d'achat</th>
                    <th className={`px-3 ${dz.head} text-right font-bold w-[12%]`}>Total</th>
                    <th className={`${dz.head} w-[150px]`}></th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[#f6f4ee] border-b border-[#8fcdb6]" data-testid="purchase-entry-row">
                    <td colSpan={2} className="px-2 py-2">
                      <ProductPicker ref={pickerRef} products={products} value={searchTerm} onChange={(v) => { setSearchTerm(v); if (pending.product && v !== pending.product.name) setPending({ product: null, qty_raw: "", price_raw: "" }); }} onPick={pickPending} onTab={focusList} placeholder="Tapez le nom ou la référence de l'article…" requireStock={false} className="[&_input]:h-9 [&_input]:rounded-md [&_input]:border-[#a9d9c6] [&_input]:bg-white" />
                    </td>
                    <td className="px-2 py-2"><div className={`h-9 px-3 flex items-center rounded-md border border-[#a9d9c6] bg-white text-sm ${pending.product ? "text-slate-900" : "text-slate-400"}`} data-testid="purchase-entry-unit">{pending.product?.unit || "Unité"}</div></td>
                    <td className="px-2 py-2">
                      <Input ref={pendingQtyRef} type="text" inputMode="decimal" value={pending.qty_raw} placeholder="Quantité" onChange={(e) => setPending({ ...pending, qty_raw: e.target.value })} onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!pending.product && !pending.qty_raw && cart.length > 0) { handleSave(); return; } setFocusTarget({ field: "pprice" }); } }}
                        className="h-9 rounded-md border-[#a9d9c6] bg-white text-right font-mono" data-testid="purchase-entry-qty" />
                    </td>
                    <td className="px-2 py-2">
                      <Input ref={pendingPriceRef} type="text" inputMode="decimal" value={pending.price_raw} placeholder="Prix d'achat" onChange={(e) => setPending({ ...pending, price_raw: e.target.value })} onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitPending(); } }}
                        className="h-9 rounded-md border-[#a9d9c6] bg-white text-right font-mono" data-testid="purchase-entry-price" />
                      {pending.product && lastPurchase[pending.product.id] && <p className="text-[10px] text-slate-500 text-right mt-0.5">Dernier achat : {money(lastPurchase[pending.product.id].unit_cost)}</p>}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-sm text-slate-700" data-testid="purchase-entry-total">{pending.product ? money((parseNum(pending.qty_raw) || 0) * (parseNum(pending.price_raw) || 0)) : ""}</td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1.5 justify-end">
                        <Button type="button" variant="outline" onClick={commitPending} className="h-9 rounded-md bg-white border-[#a9d9c6] text-slate-800 px-3" data-testid="purchase-entry-add-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
                        <Button type="button" variant="outline" onClick={() => (winOpen ? setWinOpen(false) : focusList())} className="h-9 w-10 rounded-md bg-white border-[#a9d9c6] px-0" title="Liste des articles (F4)" data-testid="toggle-articles-panel-btn"><PackageSearch className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                  {cart.map(lineRow)}
                  {cart.length === 0 && <tr><td colSpan={7} className="text-center py-5 text-slate-500" data-testid="purchase-empty-cart">Panier vide</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 px-2 pt-2">Astuce : Entrée ajoute la ligne · Entrée sur quantité vide (ou Ctrl+Entrée) valide l'achat · la désignation de chaque ligne est modifiable · F4 ouvre la liste des articles</p>
          </div>

          <div className="border-t border-[#e4e0d4]" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid="purchase-totals-section">
            <div className="lg:col-span-5">
              <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">{L.payment}</Label>
              {editing ? (
                <div className={`h-10 px-3 flex items-center rounded-md border text-xs font-bold uppercase ${paymentButtonClass(editing.amount_paid > 0 ? editing.payment_method : "non_paye", true)}`} data-testid="purchase-edit-notice">{editing.amount_paid > 0 ? `${PAYMENT_METHODS.find((m) => m.value === editing.payment_method)?.label || editing.payment_method} · ${money(editing.amount_paid)} payés` : "Non payé (à crédit)"}</div>
              ) : (
                <PaymentModeButtons value={paymentMethod} onChange={(v) => { setPaymentMethod(v); setPartial(false); setAmountPaid(0); }} methods={PAYMENT_METHODS} testPrefix="purchase-payment" />
              )}
            </div>
            <div className="lg:col-span-4 lg:pt-7 space-y-2">
              {!editing && !isUnpaid && (
                <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer select-none h-10">
                  <input type="checkbox" checked={partial} onChange={(e) => { setPartial(e.target.checked); setAmountPaid(0); }} className="w-4 h-4 accent-teal-600" data-testid="purchase-partial-checkbox" />
                  Acompte (paiement partiel)
                </label>
              )}
              {!editing && !isUnpaid && partial && (
                <div className="flex gap-1 items-center">
                  <Label className="text-xs uppercase tracking-wide text-slate-600 whitespace-nowrap">Montant payé</Label>
                  <Input type="number" value={amountPaid} onChange={(e) => setAmountPaid(Math.max(0, parseFloat(e.target.value) || 0))} min="0" className={`${inp} font-mono h-9`} data-testid="purchase-amount-paid" />
                  <Button variant="outline" size="sm" onClick={() => setAmountPaid(total)} className="h-9 text-xs text-teal-700 bg-white whitespace-nowrap rounded-md" data-testid="purchase-exact-btn">Total</Button>
                </div>
              )}
              {!editing && !isUnpaid && (
                <p className="text-xs text-teal-800" data-testid="purchase-payment-notice">
                  {paymentMethod === "especes" ? "Sortie de caisse enregistrée immédiatement." : paymentMethod === "banque" ? "Écriture ajoutée au Journal Banque." : "Règlement enregistré au compte du fournisseur."}
                  {balanceDue > 0 ? ` Acompte : le reste (${money(balanceDue)}) sera dû sur la facture fournisseur.` : " Achat réglé en totalité."}
                </p>
              )}
              {!editing && isUnpaid && <p className="text-xs text-red-700 pt-2" data-testid="purchase-unpaid-notice">Non payé = achat à crédit : aucun règlement maintenant, le total ({money(total)}) sera dû au fournisseur à la facturation.</p>}
            </div>
            <div className="lg:col-span-3 text-right space-y-0.5">
              <p className="text-sm text-slate-500">Quantité totale <span className="font-mono" data-testid="purchase-total-qty">{totalQty}</span></p>
              <p className="text-sm text-slate-500">{editing ? "Déjà payé" : "Montant payé"} <span className="font-mono" data-testid="purchase-paid">{money(effectivePaid)}</span></p>
              <p className="font-serif text-4xl text-slate-900 leading-none pt-1"><span data-testid="purchase-total">{money(total).replace(/\s?F\s?CFA/, "")}</span> <span className="text-xl text-slate-600">FCFA</span></p>
              {balanceDue > 0 && <p className="text-sm text-orange-600 font-medium">Reste à payer <span className="font-mono" data-testid="purchase-balance-due">{money(balanceDue)}</span></p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3" data-testid="purchase-action-buttons">
            <Button variant="outline" onClick={resetAll} className="h-12 rounded-md bg-white border-[#a9d9c6] text-slate-800 text-base" data-testid="purchase-cancel-btn"><XCircle className="w-4 h-4 mr-2" /> {L.cancel}</Button>
            <Button onClick={handleSave} disabled={saveDisabled} className="h-12 rounded-md text-base text-white bg-[#6cc9bb] hover:bg-[#55b9aa] disabled:bg-[#a9dfd6] disabled:opacity-100" data-testid="purchase-save-btn">
              {processing ? <div className="spinner w-4 h-4" /> : <><ClipboardList className="w-4 h-4 mr-2" /> {editing ? L.save : "Valider l'achat"}</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ArticlesWindow ref={winRef} open={winOpen} onClose={(refocus = true) => { setWinOpen(false); if (refocus) setFocusTarget({ field: "picker" }); }} products={products} searchTerm={searchTerm} onSearch={setSearchTerm} onPick={(p) => pickPending(p)} onBarcode={handleBarcode} money={(a) => money(a)} fontClass={fontClass} title={L.articles} priceKey="cost_price" allowOutOfStock lastPurchase={lastPurchase} />

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-sm" data-testid="quick-supplier-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Nouveau fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline">Nom</Label><Input value={quick.name} onChange={(e) => setQuick({ ...quick, name: e.target.value })} autoFocus className="rounded-md h-9" data-testid="quick-supplier-name" /></div>
            <div><Label className="overline">Téléphone</Label><Input value={quick.phone} onChange={(e) => setQuick({ ...quick, phone: e.target.value })} onKeyDown={(e) => e.key === "Enter" && createQuickSupplier()} className="rounded-md h-9 font-mono" data-testid="quick-supplier-phone" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-md" onClick={() => setQuickOpen(false)}>Annuler</Button><Button className="bg-[#6cc9bb] hover:bg-[#55b9aa] text-white rounded-md" disabled={!quick.name.trim()} onClick={createQuickSupplier} data-testid="quick-supplier-submit">Créer</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {isAdmin && <InvoiceUiCustomizer open={customizeOpen} onOpenChange={setCustomizeOpen} ui={ui} update={updateUi} onSave={() => saveUi()} onReset={resetUi} onCancel={revertUi} />}
    </div>
  );
};

export default PurchaseEntryPage;
